
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X } from 'lucide-react';
import { ResellerPlanDetails } from '@/hooks/license/useResellerPlans';
import { formatCurrency } from '@/utils/formatters';

interface OrderSummaryProps {
  planDetails?: ResellerPlanDetails;
  promoCode: string;
  promoDiscount: number;
  setPromoCode: (code: string) => void;
  setPromoDiscount: (discount: number) => void;
  orderNumber?: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  planDetails,
  promoCode,
  promoDiscount,
  setPromoCode,
  setPromoDiscount,
  orderNumber
}) => {
  const [inputPromoCode, setInputPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  const setupFee = planDetails?.setupFee || 500;
  const annualPrice = planDetails?.annualPrice || 999;
  
  const handleApplyPromo = () => {
    setApplyingPromo(true);
    setPromoMessage(null);
    
    // Simulate promo code validation with a delay
    setTimeout(() => {
      if (inputPromoCode === 'LAUNCH25') {
        const discount = Math.round(setupFee * 0.25); // 25% off
        setPromoCode(inputPromoCode);
        setPromoDiscount(discount);
        setPromoMessage({ type: 'success', message: '25% discount applied!' });
        
        // Store in session storage for persistence
        sessionStorage.setItem('promoCode', inputPromoCode);
        sessionStorage.setItem('promoDiscount', String(discount));
      } else if (inputPromoCode === 'REFERME50') {
        const discount = Math.round(setupFee * 0.5); // 50% off
        setPromoCode(inputPromoCode);
        setPromoDiscount(discount);
        setPromoMessage({ type: 'success', message: '50% discount applied!' });
        
        // Store in session storage for persistence
        sessionStorage.setItem('promoCode', inputPromoCode);
        sessionStorage.setItem('promoDiscount', String(discount));
      } else {
        setPromoMessage({ type: 'error', message: 'Invalid promo code' });
      }
      setApplyingPromo(false);
    }, 1000);
  };
  
  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoDiscount(0);
    setInputPromoCode('');
    setPromoMessage(null);
    
    // Remove from session storage
    sessionStorage.removeItem('promoCode');
    sessionStorage.removeItem('promoDiscount');
  };
  
  return (
    <Card className="border-primary-blue/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">Order Summary</CardTitle>
        {orderNumber && (
          <p className="text-sm font-medium text-gray-500">Order #: {orderNumber}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between pb-2 border-b border-gray-200">
            <span className="text-gray-600">Plan:</span>
            <span className="font-semibold">{planDetails?.name || 'Reseller Pro'}</span>
          </div>
          
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">Setup Fee:</span>
            <span className="font-semibold">${formatCurrency(setupFee)}</span>
          </div>
          
          {promoDiscount > 0 && (
            <div className="flex justify-between items-center pb-2 border-b border-gray-200 text-green-600">
              <span className="flex items-center">
                <Check className="w-4 h-4 mr-1" />
                Discount ({promoCode}):
              </span>
              <span className="font-semibold">-${formatCurrency(promoDiscount)}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center pb-2 border-b border-gray-200 text-lg">
            <span className="font-semibold">Total Due Now:</span>
            <span className="font-bold">${formatCurrency(setupFee - promoDiscount)}</span>
          </div>
          
          <div className="pt-3 pb-2 border-b border-gray-200">
            <div className="flex justify-between items-center text-gray-700">
              <span>Annual Subscription:</span>
              <span className="font-medium">${formatCurrency(annualPrice)}/year</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              First payment due in 30 days after setup
            </p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600 mb-2">
              {promoCode ? 'Promo code applied:' : 'Have a promo code?'}
            </p>
            
            {promoCode ? (
              <div className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                <div className="flex items-center">
                  <Check className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-green-700 font-medium">{promoCode}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleRemovePromo}
                  className="h-8 text-gray-500 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input 
                  placeholder="Enter promo code"
                  value={inputPromoCode}
                  onChange={(e) => setInputPromoCode(e.target.value)}
                  className="h-9"
                />
                <Button 
                  onClick={handleApplyPromo} 
                  disabled={!inputPromoCode || applyingPromo}
                  className="whitespace-nowrap h-9"
                >
                  Apply
                </Button>
              </div>
            )}
            
            {promoMessage && (
              <p className={`text-sm mt-1 ${promoMessage.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {promoMessage.message}
              </p>
            )}
          </div>
          
          <div className="pt-2 text-sm text-gray-600">
            <p className="mb-1">By proceeding with this order, you agree to our:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Terms of Service</li>
              <li>Reseller Program Agreement</li>
              <li>Privacy Policy</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
